import MultiSelectDropdown, { Option } from "@crescentParts/Components/MultiSelectDropDown";
import subscribeNewsletter from "@crescentParts/Handlers/Custom/CustomNewsletterHandler";
import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCreateAccountReturnUrl } from "@insite/client-framework/Store/Pages/CreateAccount/CreateAccountSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import {
    SearchFieldWrapperStyles,
} from "@insite/content-library/Widgets/OrderHistory/SearchFieldWrapper";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconPresentationProps } from "@insite/mobius/Icon";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { ReactNode, useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    subscribeNewsletter,
};

const mapStateToProps = (state: ApplicationState) => ({
    accountSettings: getSettingsCollection(state).accountSettings,
    returnUrl: getCreateAccountReturnUrl(state),
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CreateAccountStyles {
    form?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    centeringWrapper?: InjectableCss;
    mainGridContainer?: GridContainerProps;
    userGridItem?: GridItemProps;
    userGridContainer?: GridContainerProps;
    formFieldGridItem?: GridItemProps;
    formFieldGridItemMultiSelect?: GridItemProps;
    formTextField?: TextFieldPresentationProps;
    userNameGridItem?: GridItemProps;
    userNameTextField?: TextFieldPresentationProps;
    passwordGridItem?: GridItemProps;
    passwordTextField?: TextFieldPresentationProps;
    passwordIcon?: IconPresentationProps;
    confirmPasswordTextField?: TextFieldPresentationProps;
    confirmPasswordIcon?: IconPresentationProps;
    submitErrorGridItem?: GridItemProps;
    submitErrorTitle?: TypographyProps;
    submitGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    passwordRequirementsGridItem?: GridItemProps;
    passwordRequirementsGridContainer?: GridContainerProps;
    passwordGridContainer?: GridContainerProps;
    wrapper?: SearchFieldWrapperStyles;
}

export const createAccountStyles: CreateAccountStyles = {
    form: {
        css: css`
            background: rgba(225, 221, 226, 0.3);
            border-radius: 8px;
            > [class^="GridWrapper"] {
                [class^="GridOffset"] {
                    margin: 0;
                    width: 100% !important;
                    [class^="GridWrapper"] {
                        max-width: none;
                    }
                }
            }
        `,
    },
    centeringWrapper: {
        css: css`
            display: flex;
            height: 300px;
            justify-content: center;
            align-items: center;
        `,
    },
    userGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
            padding: 30px;
        `,
    },
    userGridContainer: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            max-width: 300px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    formFieldGridItem: {
        width: 12,
    },
    userNameGridItem: {
        width: 12,
    },
    passwordGridItem: {
        width: 12,
    },
    submitErrorGridItem: {
        width: 12,
    },
    submitErrorTitle: {
        color: "danger",
    },
    submitGridItem: {
        width: 12,
    },
    passwordRequirementsGridContainer: {
        gap: 5,
    },
    passwordRequirementsGridItem: {
        width: 12,
    },
    formTextField: {
        css: css`
            border-radius: 8px !important;
        `,
    },
    formFieldGridItemMultiSelect: {
        width: 12,
        css: css`
            .multi-select-dd {
                background-color: #FFFFFF;
            }
            .error {
                border: 2px solid #DC3636;
            }
        `
    },
};

const styles = createAccountStyles;
const emailRegexp = new RegExp("\\w+([-+.']\\w+)*@\\w+([-.]\\w+)*\\.\\w+([-.]\\w+)*");
const StyledForm = getStyledWrapper("form");
const typeOfWorkList = [
    { "workStatus": "0", "displayName": "STL Metro"},
    { "workStatus": "1", "displayName": "Training"},
    { "workStatus": "2", "displayName": "Light Commercial"},
    { "workStatus": "3", "displayName": "Refrigeration"},
    { "workStatus": "4", "displayName": "Residential"},
    { "workStatus": "5", "displayName": "American Standard Dealer",},
    { "workStatus": "6", "displayName": "Mitsubishi Dealer" },
    { "workStatus": "7", "displayName": "Schools" }
]
const NewsLetter = ({ subscribeNewsletter }: Props) => {
    const userNameRequiredFieldMessage = siteMessage("CreateNewAccountInfo_UserName_Required");
    const emailRequiredFieldMessage = siteMessage("CreateNewAccountInfo_EmailAddress_Required");
    const emailFieldMessage = siteMessage("CreateNewAccountInfo_EmailAddress_ValidEmail");
    const confirmEmailRequiredFieldMessage = siteMessage("Confirm Email Address is required");

    const firstNameRequiredFieldMessage = siteMessage("First Name is required");
    const lastNameRequiredFieldMessage = siteMessage("Last Name is required");
    const jobTitleRequiredFieldMessage = siteMessage("Job Title is required");
    const companyRequiredFieldMessage = siteMessage("Company is required");
    const address1RequiredFieldMessage = siteMessage("Address 1 is required");
    const cityRequiredFieldMessage = siteMessage("City is required");
    const stateRequiredFieldMessage = siteMessage("State is required");
    const zipRequiredFieldMessage = siteMessage("Zip is required");
    const typeOfWorkRequiredFieldMessage = siteMessage("Type of work is required");

    const [firstName, setFirstName] = useState("");
    const [firstNameError, setFirstNameError] = useState(firstNameRequiredFieldMessage);
    const [lastName, setLastName] = useState("");
    const [lastNameError, setLastNameError] = useState(lastNameRequiredFieldMessage);
    const [jobTitle, setJobTitle] = useState("");
    const [jobTitleError, setJobTitleError] = useState(jobTitleRequiredFieldMessage);
    const [company, setCompany] = useState("");
    const [companyError, setCompanyError] = useState(companyRequiredFieldMessage);
    const [address1, setAddress1] = useState("");
    const [address1Error, setAddress1Error] = useState(address1RequiredFieldMessage);
    const [address2, setAddress2] = useState("");
    const [city, setCity] = useState("");
    const [cityError, setCityError] = useState(cityRequiredFieldMessage);
    const [state, setState] = useState("");
    const [stateError, setStateError] = useState(stateRequiredFieldMessage);
    const [zip, setZip] = useState("");
    const [zipError, setZipError] = useState(zipRequiredFieldMessage);
    const [typeOfWork, setTypeOfWork] = useState<string[]>([]);
    const [typeOfWorkError, setTypeOfWorkError] = useState(typeOfWorkRequiredFieldMessage);
    const [storeLocation, setStoreLocation] = useState("");
    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState(emailRequiredFieldMessage);
    const [confirmEmail, setConfirmEmail] = useState("");
    const [confirmEmailError, setConfirmEmailError] = useState<ReactNode>(confirmEmailRequiredFieldMessage);
    const [errorMessage, setErrorMessage] = useState("");
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const toasterContext = useContext(ToasterContext);

    const validateFirstName = (name: string) => {
        const errorMessage = !name ? firstNameRequiredFieldMessage : "";
        setFirstNameError(errorMessage);
        return !errorMessage;
    };

    const validateLastName = (value: string) => {
        const errorMessage = !value ? lastNameRequiredFieldMessage : "";
        setLastNameError(errorMessage);
        return !errorMessage;
    };

    const validateJobTitle = (value: string) => {
        const errorMessage = !value ? jobTitleRequiredFieldMessage : "";
        setJobTitleError(errorMessage);
        return !errorMessage;
    };

    const validateCompany = (value: string) => {
        const errorMessage = !value ? companyRequiredFieldMessage : "";
        setCompanyError(errorMessage);
        return !errorMessage;
    };

    const validateAddress1 = (value: string) => {
        const errorMessage = !value ? address1RequiredFieldMessage : "";
        setAddress1Error(errorMessage);
        return !errorMessage;
    };

    const validateCity = (value: string) => {
        const errorMessage = !value ? cityRequiredFieldMessage : "";
        setCityError(errorMessage);
        return !errorMessage;
    };

    const validateState = (value: string) => {
        const errorMessage = !value ? stateRequiredFieldMessage : "";
        setStateError(errorMessage);
        return !errorMessage;
    };

    const validateZip = (value: string) => {
        const errorMessage = !value ? zipRequiredFieldMessage : "";
        setZipError(errorMessage);
        return !errorMessage;
    };

    const validateTypeOfWork = () => {
        const errorMessage = typeOfWork.length === 0 ? typeOfWorkRequiredFieldMessage : "";
        setTypeOfWorkError(errorMessage);
        return !errorMessage;
    };

    const validateEmail = (email: string) => {
        const errorMessage = !email ? emailRequiredFieldMessage : emailRegexp.test(email) ? "" : emailFieldMessage;
        !confirmEmail
            ? ""
            : email !== confirmEmail
            ? setConfirmEmailError(siteMessage("Email does not match"))
            : setConfirmEmailError("");
        setEmailError(errorMessage);
        return !errorMessage;
    };

    const validateConfirmEmail = (confirmEmail: string) => {
        const errorMessage = email !== confirmEmail ? siteMessage("Email does not match") : "";
        setConfirmEmailError(errorMessage);
        return !errorMessage;
    };

    const validateSubmitEnabled = () => {
        return !(
            firstNameError ||
            lastNameError ||
            jobTitleError ||
            companyError ||
            address1Error ||
            cityError ||
            stateError ||
            zipError ||
            typeOfWorkError ||
            emailError ||
            confirmEmailError
        );
    };

    const firstNameChangeHandler = (value: string) => {
        validateFirstName(value);
        setFirstName(value);
    };

    const lastNameChangeHandler = (value: string) => {
        validateLastName(value);
        setLastName(value);
    };

    const jobTitleChangeHandler = (value: string) => {
        validateJobTitle(value);
        setJobTitle(value);
    };

    const companyChangeHandler = (value: string) => {
        validateCompany(value);
        setCompany(value);
    };

    const address1ChangeHandler = (value: string) => {
        validateAddress1(value);
        setAddress1(value);
    };

    const address2ChangeHandler = (value: string) => {
        setAddress2(value);
    };

    const cityChangeHandler = (value: string) => {
        validateCity(value);
        setCity(value);
    };

    const stateChangeHandler = (value: string) => {
        validateState(value);
        setState(value);
    };

    const zipChangeHandler = (value: string) => {
        validateZip(value);
        setZip(value);
    };

    const handleChangeTypeOfWork = (option: Option) => {
        const isSelected = typeOfWork && typeOfWork.length > 0 && typeOfWork.indexOf(option.value) > -1;
        let status: string[] = typeOfWork && typeOfWork.length > 0
            ? [...typeOfWork]
            : [];
        if (isSelected) {
            status = typeOfWork!.filter(val => option.value !== val);
        } else {
            status.push(option.value);
        }
        if (status.length > 0) {
            setTypeOfWork(status);
        } else {
            setTypeOfWork([]);
        }
        validateTypeOfWork();
        setIsSubmitted(false);
    };

    const storeLocationChangeHandler = (value: string) => {
        setStoreLocation(value);
    };

    const emailChangeHandler = (email: string) => {
        validateEmail(email);
        setEmail(email);
    };

    const confirmEmailChangeHandler = (email: string) => {
        validateConfirmEmail(email);
        setConfirmEmail(email);
    };

    const onSuccessHandler = () => {
        setErrorMessage("");
        toasterContext.addToast({ body: translate("Successfully submitted!"), messageType: "success" });
        clearFormFields();
    };

    const clearFormFields = () => {
        setFirstName("");
        setLastName("");
        setJobTitle("");
        setCompany("");
        setAddress1("");
        setAddress2("");
        setCity("");
        setState("");
        setZip("");
        setTypeOfWork([]);
        setStoreLocation("");
        setEmail("");
        setConfirmEmail("");
        setErrorMessage("");
        setIsSubmitted(false);
        setIsSubmitting(false);
        setFirstNameError(firstNameRequiredFieldMessage);
        setLastNameError(lastNameRequiredFieldMessage);
        setJobTitleError(jobTitleRequiredFieldMessage);
        setCompanyError(companyRequiredFieldMessage);
        setAddress1Error(address1RequiredFieldMessage);
        setCityError(cityRequiredFieldMessage);
        setStateError(stateRequiredFieldMessage);
        setZipError(zipRequiredFieldMessage);
        setTypeOfWorkError(typeOfWorkRequiredFieldMessage);
        setEmailError(emailRequiredFieldMessage);
    };

    const submitHandler = (event: any) => {
        event.preventDefault();

        if (!isSubmitted) {
            setIsSubmitted(true);
        }
        if(!validateTypeOfWork()) {
            return false;
        }
        if (!validateSubmitEnabled()) {
            return;
        }

        setIsSubmitting(true);

        subscribeNewsletter({
            firstName,
            lastName,
            jobTitle,
            company,
            address1,
            address2,
            city,
            state,
            zip,
            typeOfWork: typeOfWork.map((workType: string) => {
                return typeOfWorkList.filter((work) => work.workStatus === workType)[0].displayName
            }).join(","),
            storeLocation,
            email,
            onSuccess: onSuccessHandler,
            onError: error => {
                setErrorMessage(error);
                setIsSubmitting(false);
                const errorMsg = error.toString().length > 400 ? "Something went wrong!" : error;
                toasterContext.addToast({ body: translate(errorMsg), messageType: "danger" });
            },
        });
    };

    const isSubmitEnabled = validateSubmitEnabled();
    const options = typeOfWorkList;
    const uniqueOptions = new Map<string, string[]>();
    for (const option of options) {
        const workTypeStatuses = uniqueOptions.get(option.displayName) || [];
        workTypeStatuses.push(option.workStatus);
        uniqueOptions.set(option.displayName, workTypeStatuses);
    }

    const dropDownOptions: Option[] = Array.from(uniqueOptions, ([name, values]) => ({ name, values })).map(
        option => ({
            value: option.values.join(","),
            label: option.name,
        }),
    );

    const selectedOptions: Option[] =
        typeOfWork && typeOfWork.length > 0
            ? typeOfWork.map(val => ({
                value: val,
                label: dropDownOptions.find(opt => opt.value === val)?.label || "",
            }))
            : [];

    return (
        <StyledForm {...styles.form} onSubmit={submitHandler} noValidate>
            <GridContainer {...styles.mainGridContainer}>
                <GridItem {...styles.userGridItem}>
                    <GridContainer {...styles.userGridContainer}>
                        <GridItem {...styles.formFieldGridItem}>
                            <TextField
                                {...styles.formTextField}
                                type="text"
                                label={translate("First Name")}
                                onChange={e => firstNameChangeHandler(e.currentTarget.value)}
                                value={firstName}
                                data-test-selector="newsletter_firstname"
                                required
                                error={isSubmitted && firstNameError}
                            />
                        </GridItem>
                        <GridItem {...styles.formFieldGridItem}>
                            <TextField
                                {...styles.formTextField}
                                type="text"
                                label={translate("Last Name")}
                                onChange={e => lastNameChangeHandler(e.currentTarget.value)}
                                value={lastName}
                                data-test-selector="newsletter_lastname"
                                required
                                error={isSubmitted && lastNameError}
                            />
                        </GridItem>
                        <GridItem {...styles.formFieldGridItem}>
                            <TextField
                                {...styles.formTextField}
                                type="text"
                                label={translate("Job Title")}
                                onChange={e => jobTitleChangeHandler(e.currentTarget.value)}
                                value={jobTitle}
                                data-test-selector="newsletter_jobtitle"
                                required
                                error={isSubmitted && jobTitleError}
                            />
                        </GridItem>

                        <GridItem {...styles.formFieldGridItem}>
                            <TextField
                                {...styles.formTextField}
                                type="text"
                                label={translate("Company")}
                                onChange={e => companyChangeHandler(e.currentTarget.value)}
                                value={company}
                                data-test-selector="newsletter_company"
                                required
                                error={isSubmitted && companyError}
                            />
                        </GridItem>

                        <GridItem {...styles.formFieldGridItem}>
                            <TextField
                                {...styles.formTextField}
                                type="text"
                                label={translate("Address 1")}
                                onChange={e => address1ChangeHandler(e.currentTarget.value)}
                                value={address1}
                                data-test-selector="newsletter_address_1"
                                required
                                error={isSubmitted && address1Error}
                            />
                        </GridItem>

                        <GridItem {...styles.formFieldGridItem}>
                            <TextField
                                {...styles.formTextField}
                                type="text"
                                label={translate("Address 2")}
                                onChange={e => address2ChangeHandler(e.currentTarget.value)}
                                value={address2}
                                data-test-selector="newsletter_address_2"
                            />
                        </GridItem>

                        <GridItem {...styles.formFieldGridItem}>
                            <TextField
                                {...styles.formTextField}
                                type="text"
                                label={translate("City")}
                                onChange={e => cityChangeHandler(e.currentTarget.value)}
                                value={city}
                                data-test-selector="newsletter_city"
                                required
                                error={isSubmitted && cityError}
                            />
                        </GridItem>

                        <GridItem {...styles.formFieldGridItem}>
                            <TextField
                                {...styles.formTextField}
                                type="text"
                                label={translate("State")}
                                onChange={e => stateChangeHandler(e.currentTarget.value)}
                                value={state}
                                data-test-selector="newsletter_state"
                                required
                                error={isSubmitted && stateError}
                            />
                        </GridItem>

                        <GridItem {...styles.formFieldGridItem}>
                            <TextField
                                {...styles.formTextField}
                                type="text"
                                label={translate("Zip Code")}
                                onChange={e => zipChangeHandler(e.currentTarget.value)}
                                value={zip}
                                data-test-selector="newsletter_zip"
                                required
                                error={isSubmitted && zipError}
                            />
                        </GridItem>

                        <GridItem {...styles.formFieldGridItemMultiSelect}>
                            <MultiSelectDropdown
                                label={translate(
                                    "Please select the categories that apply to you or your company. *",
                                )}
                                handleOptionClick={handleChangeTypeOfWork}
                                options={dropDownOptions}
                                selectedOptions={selectedOptions}
                                error={isSubmitted && typeOfWorkError}
                            />
                        </GridItem>

                        <GridItem {...styles.formFieldGridItem}>
                            <TextField
                                {...styles.formTextField}
                                type="text"
                                label={translate("Crescent Store Location(s) Used Most Often")}
                                onChange={e => storeLocationChangeHandler(e.currentTarget.value)}
                                value={storeLocation}
                                data-test-selector="newsletter_store_location"
                            />
                        </GridItem>

                        <GridItem {...styles.formFieldGridItem}>
                            <TextField
                                {...styles.formTextField}
                                type="text"
                                label={translate("Email")}
                                onChange={e => emailChangeHandler(e.currentTarget.value)}
                                value={email}
                                data-test-selector="newsletter_email"
                                required
                                error={isSubmitted && emailError}
                            />
                        </GridItem>

                        <GridItem {...styles.formFieldGridItem}>
                            <TextField
                                {...styles.formTextField}
                                type="text"
                                label={translate("Confirm Email")}
                                onChange={e => confirmEmailChangeHandler(e.currentTarget.value)}
                                value={confirmEmail}
                                data-test-selector="newsletter_confirm_email"
                                required
                                error={isSubmitted && confirmEmailError}
                            />
                        </GridItem>

                        {/* {errorMessage && (
                            <GridItem data-test-selector="newsleter_ErrorMessage" {...styles.submitErrorGridItem}>
                                <Typography {...styles.submitErrorTitle}>{errorMessage}</Typography>
                            </GridItem>
                        )} */}
                        <GridItem {...styles.submitGridItem}>
                            <Button
                                {...styles.submitButton}
                                type="submit"
                                disabled={isSubmitting || (isSubmitted && !isSubmitEnabled)}
                                data-test-selector="createAccount_createButton"
                            >
                                {translate("Submit")}
                            </Button>
                        </GridItem>
                    </GridContainer>
                </GridItem>
            </GridContainer>
        </StyledForm>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(NewsLetter),
    definition: {
        group: "Common",
    },
};

export default widgetModule;
